import { useState, useEffect, useCallback } from 'react';

const SELECTED_TEAM_STORAGE_KEY = 'trustpulse_selected_team_id';

type Team = {
  id: number;
  name: string;
};

type UsePersistedTeamSelectionReturn = {
  selectedTeamId: number;
  setSelectedTeamId: (teamId: number) => void;
};

export const usePersistedTeamSelection = (
  teams: Team[] = [],
): UsePersistedTeamSelectionReturn => {
  const [selectedTeamId, setSelectedTeamIdState] = useState<number>(0);
  // const [selectedTeamId, setSelectedTeamIdState] = useState<number>(() => {
  //   return retrieveStoredTeamId() ?? 0;
  // });

  const selectTeamAndPersist = useCallback((teamId: number) => {
    setSelectedTeamIdState(teamId);
    saveTeamIdToStorage(teamId);
  }, []);

  const restoreValidStoredTeam = useCallback((storedTeamId: number) => {
    setSelectedTeamIdState(storedTeamId);
  }, []);

  const selectFirstTeamAsDefault = useCallback(() => {
    const firstTeamId = getFirstTeamId(teams);
    if (firstTeamId) setSelectedTeamIdState(firstTeamId);
  }, [teams]);

  const clearInvalidTeamAndSelectFirst = useCallback(() => {
    clearStoredTeamId();
    selectFirstTeamAsDefault();
  }, [selectFirstTeamAsDefault]);

  // Without teams, no team is chosen. The stored team was chosen in this browser, perhaps by someone else, and is only
  // used once it is among the user's own teams.
  const selectNoTeam = useCallback(() => {
    if (selectedTeamId !== 0) setSelectedTeamIdState(0);
  }, [selectedTeamId]);

  const initializeTeamSelectionFromStorage = useCallback(() => {
    const storedTeamId = retrieveStoredTeamId();

    const hasNoStoredTeam = !storedTeamId;
    if (hasNoStoredTeam) {
      selectFirstTeamAsDefault();
      return;
    }

    const isStoredTeamValid = findTeamById(teams, storedTeamId);
    if (isStoredTeamValid) {
      restoreValidStoredTeam(storedTeamId);
      return;
    }

    clearInvalidTeamAndSelectFirst();
  }, [
    teams,
    selectFirstTeamAsDefault,
    restoreValidStoredTeam,
    clearInvalidTeamAndSelectFirst,
  ]);

  const validateCurrentTeamSelection = useCallback(() => {
    const isCurrentTeamInvalid = !findTeamById(teams, selectedTeamId);
    if (isCurrentTeamInvalid) {
      clearInvalidTeamAndSelectFirst();
    }
  }, [teams, selectedTeamId, clearInvalidTeamAndSelectFirst]);

  const handleNoTeamSelected = useCallback(() => {
    initializeTeamSelectionFromStorage();
  }, [initializeTeamSelectionFromStorage]);

  const handleTeamAlreadySelected = useCallback(() => {
    validateCurrentTeamSelection();
  }, [validateCurrentTeamSelection]);

  const handleTeamsLoaded = useCallback(() => {
    const hasNoTeamSelected = selectedTeamId === 0;

    if (hasNoTeamSelected) {
      handleNoTeamSelected();
      return;
    }

    handleTeamAlreadySelected();
  }, [selectedTeamId, handleNoTeamSelected, handleTeamAlreadySelected]);

  const handleTeamsNotLoaded = useCallback(() => {
    selectNoTeam();
  }, [selectNoTeam]);

  useEffect(() => {
    const areTeamsLoaded = teams.length > 0;

    if (areTeamsLoaded) {
      handleTeamsLoaded();
      return;
    }

    handleTeamsNotLoaded();
  }, [teams, handleTeamsLoaded, handleTeamsNotLoaded]);

  return {
    selectedTeamId,
    setSelectedTeamId: selectTeamAndPersist,
  };
};

function retrieveStoredTeamId(): number | null {
  try {
    const savedTeamId = localStorage.getItem(SELECTED_TEAM_STORAGE_KEY);
    if (!savedTeamId) return null;

    const teamId = parseInt(savedTeamId, 10);
    return isNaN(teamId) || teamId <= 0 ? null : teamId;
  } catch {
    return null;
  }
}

function clearStoredTeamId(): void {
  try {
    localStorage.removeItem(SELECTED_TEAM_STORAGE_KEY);
  } catch {
    // Silent fail
  }
}

function saveTeamIdToStorage(teamId: number): void {
  if (teamId <= 0) return;

  try {
    localStorage.setItem(SELECTED_TEAM_STORAGE_KEY, teamId.toString());
  } catch {
    // Silent fail
  }
}

function findTeamById(teams: Team[], teamId: number): boolean {
  return teams.some((team) => team.id === teamId);
}

function getFirstTeamId(teams: Team[]): number | null {
  return teams[0]?.id || null;
}
